import type {HeadProps} from 'gatsby'
import React from 'react'
import type {ReactNode} from 'react'

import favicon from '@helios/shared/images/icons/favicon.ico'
import type {LocaleCode} from '@helios/shared/locale/locales'
import {useLocale, getDefaultLocale} from '@helios/shared/providers/locale'
import {socialHandles} from '@helios/shared/site-config'
import type Locale from '@helios/shared/types/locale'
import type {PageContext} from '@helios/shared/types/page-context'

export type Meta = {
    title: string
    description: string
    openGraphImageUrl: string
}

export type Props = Partial<Meta> & {
    siteName: string
    preventIndexing?: boolean
    fallback: Required<Meta>
    locales: Locale[]
    location: HeadProps['location']
    pageContext: PageContext
    children?: ReactNode
}

export const SEOBase = ({
    fallback,
    siteName,
    title,
    description,
    openGraphImageUrl,
    preventIndexing,
    locales,
    location,
    pageContext,
    children,
}: Props) => {
    const currentLocale = useLocale()
    const defaultLocale = getDefaultLocale()
    const siteUrl = process.env.GATSBY_SITE_URL

    const seo = {
        title: `${title || fallback.title} - ${siteName}`,
        description: description || fallback.description,
        canonicalUrl: `${siteUrl}${location.pathname}`,
        openGraphImage: openGraphImageUrl || fallback.openGraphImageUrl,
        defaultUrl: `${siteUrl}${pageContext.paths?.[defaultLocale.code]}`,
    }

    const [ogImageCanonical, ogImageDefaultParams] = seo.openGraphImage.split('?')
    const ogImageParams = new URLSearchParams(ogImageDefaultParams)

    const noIndex =
        process.env.GATSBY_ACTIVE_ENV !== 'production' ||
        preventIndexing ||
        pageContext.preventIndexing

    const alternativeLocales = Object.entries(pageContext.paths ?? {})
        .filter(([localeCode]) =>
            locales.map((locale) => locale.code).includes(localeCode as LocaleCode)
        )
        .map(([localeCode, path]) => ({
            localeCode,
            path,
            htmlLang: locales.find((locale) => locale.code === localeCode)?.htmlLang || localeCode,
        }))

    return (
        <>
            <html lang={currentLocale.htmlLang} />

            {noIndex && <meta name="robots" content="noindex" />}

            <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=no" />
            <title>{seo.title}</title>

            <link rel="icon" href={favicon} type="image/x-icon" />

            {alternativeLocales.map(({localeCode, path, htmlLang}) => (
                <link
                    rel="alternate"
                    href={`${siteUrl}${path}`}
                    hrefLang={htmlLang}
                    key={localeCode}
                />
            ))}

            <link rel="alternate" href={seo.defaultUrl} hrefLang="x-default" />
            <link rel="canonical" href={seo.canonicalUrl} />
            <meta name="description" content={seo.description} />

            <meta name="twitter:card" content="summary" />
            <meta name="twitter:site" content={`@${socialHandles.twitter}`} />
            <meta name="twitter:creator" content={`@${socialHandles.twitter}`} />

            <meta property="fb:app_id" content={socialHandles.facebook} />

            <meta property="og:title" content={seo.title} />
            <meta property="og:description" content={seo.description} />
            <meta property="og:type" content="website" />
            <meta property="og:url" content={seo.canonicalUrl} />
            <meta property="og:site_name" content={siteName} />
            <meta
                property="og:image"
                content={`${ogImageCanonical}?${ogImageParams.toString()}&fit=crop&w=1200&h=630&dpr=2`}
            />

            {alternativeLocales.map(({localeCode, htmlLang}) => (
                <meta
                    key={localeCode}
                    property={`og:locale${localeCode === currentLocale.code ? '' : ':alternate'}`}
                    content={htmlLang.replace('-', '_')}
                />
            ))}

            <meta name="facebook-domain-verification" content="k2i8qi1dqf0k41b7r44p87smjwnhay" />

            {children}
        </>
    )
}
